'use client'

import { useState, useEffect, useCallback } from 'react'

// baby: Currency configuration with exchange rates (approximate, updated periodically)
// Base currency is USD - all prices are defined in USD and converted
export interface CurrencyInfo {
    code: string
    symbol: string
    rate: number       // Conversion rate from USD
    locale: string     // For number formatting
    position: 'prefix' | 'suffix'
}

// Supported currencies mapped by country code (ISO 3166-1 alpha-2)
const CURRENCY_BY_COUNTRY: Record<string, CurrencyInfo> = {
    // Indian Rupee
    IN: { code: 'INR', symbol: '₹', rate: 85, locale: 'en-IN', position: 'prefix' },
    // Euro
    DE: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'de-DE', position: 'prefix' },
    FR: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'fr-FR', position: 'prefix' },
    IT: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'it-IT', position: 'prefix' },
    ES: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'es-ES', position: 'prefix' },
    NL: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'nl-NL', position: 'prefix' },
    PT: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'pt-PT', position: 'prefix' },
    BE: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'fr-BE', position: 'prefix' },
    AT: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'de-AT', position: 'prefix' },
    IE: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'en-IE', position: 'prefix' },
    FI: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'fi-FI', position: 'prefix' },
    GR: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'el-GR', position: 'prefix' },
    // British Pound
    GB: { code: 'GBP', symbol: '£', rate: 0.79, locale: 'en-GB', position: 'prefix' },
    // Japanese Yen
    JP: { code: 'JPY', symbol: '¥', rate: 150, locale: 'ja-JP', position: 'prefix' },
    // Canadian Dollar
    CA: { code: 'CAD', symbol: 'C$', rate: 1.36, locale: 'en-CA', position: 'prefix' },
    // Australian Dollar
    AU: { code: 'AUD', symbol: 'A$', rate: 1.55, locale: 'en-AU', position: 'prefix' },
    // Singapore Dollar
    SG: { code: 'SGD', symbol: 'S$', rate: 1.34, locale: 'en-SG', position: 'prefix' },
    // UAE Dirham
    AE: { code: 'AED', symbol: 'AED ', rate: 3.67, locale: 'ar-AE', position: 'prefix' },
    // Brazilian Real
    BR: { code: 'BRL', symbol: 'R$', rate: 5.0, locale: 'pt-BR', position: 'prefix' },
    // South Korean Won
    KR: { code: 'KRW', symbol: '₩', rate: 1320, locale: 'ko-KR', position: 'prefix' },
    // Mexican Peso
    MX: { code: 'MXN', symbol: 'MX$', rate: 17.2, locale: 'es-MX', position: 'prefix' },
    // Indonesian Rupiah
    ID: { code: 'IDR', symbol: 'Rp', rate: 15700, locale: 'id-ID', position: 'prefix' },
    // Thai Baht
    TH: { code: 'THB', symbol: '฿', rate: 35.5, locale: 'th-TH', position: 'prefix' },
    // Malaysian Ringgit
    MY: { code: 'MYR', symbol: 'RM', rate: 4.7, locale: 'ms-MY', position: 'prefix' },
    // Philippine Peso
    PH: { code: 'PHP', symbol: '₱', rate: 56, locale: 'en-PH', position: 'prefix' },
    // Nigerian Naira
    NG: { code: 'NGN', symbol: '₦', rate: 1550, locale: 'en-NG', position: 'prefix' },
    // South African Rand
    ZA: { code: 'ZAR', symbol: 'R', rate: 18.5, locale: 'en-ZA', position: 'prefix' },
    // Turkish Lira
    TR: { code: 'TRY', symbol: '₺', rate: 32, locale: 'tr-TR', position: 'prefix' },
    // Swiss Franc
    CH: { code: 'CHF', symbol: 'CHF ', rate: 0.88, locale: 'de-CH', position: 'prefix' },
    // Swedish Krona
    SE: { code: 'SEK', symbol: 'kr', rate: 10.5, locale: 'sv-SE', position: 'suffix' },
    // Norwegian Krone
    NO: { code: 'NOK', symbol: 'kr', rate: 10.8, locale: 'nb-NO', position: 'suffix' },
    // Danish Krone
    DK: { code: 'DKK', symbol: 'kr', rate: 6.9, locale: 'da-DK', position: 'suffix' },
    // Polish Zloty
    PL: { code: 'PLN', symbol: 'zł', rate: 4.0, locale: 'pl-PL', position: 'suffix' },
    // New Zealand Dollar
    NZ: { code: 'NZD', symbol: 'NZ$', rate: 1.68, locale: 'en-NZ', position: 'prefix' },
    // Hong Kong Dollar
    HK: { code: 'HKD', symbol: 'HK$', rate: 7.82, locale: 'en-HK', position: 'prefix' },
    // Taiwan Dollar
    TW: { code: 'TWD', symbol: 'NT$', rate: 32, locale: 'zh-TW', position: 'prefix' },
    // Pakistani Rupee
    PK: { code: 'PKR', symbol: 'Rs', rate: 278, locale: 'en-PK', position: 'prefix' },
    // Bangladeshi Taka
    BD: { code: 'BDT', symbol: '৳', rate: 110, locale: 'bn-BD', position: 'prefix' },
    // Sri Lankan Rupee
    LK: { code: 'LKR', symbol: 'Rs', rate: 310, locale: 'en-LK', position: 'prefix' },
    // Kenyan Shilling
    KE: { code: 'KES', symbol: 'KSh', rate: 153, locale: 'en-KE', position: 'prefix' },
}

// Default USD currency
const USD_CURRENCY: CurrencyInfo = {
    code: 'USD',
    symbol: '$',
    rate: 1,
    locale: 'en-US',
    position: 'prefix',
}

// baby: Map IANA timezones to country codes: no API call needed!
// This covers the most common/populated timezones per country.
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
    // India
    'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
    // Japan
    'Asia/Tokyo': 'JP',
    // South Korea
    'Asia/Seoul': 'KR',
    // Singapore
    'Asia/Singapore': 'SG',
    // Hong Kong
    'Asia/Hong_Kong': 'HK',
    // Taiwan
    'Asia/Taipei': 'TW',
    // Thailand
    'Asia/Bangkok': 'TH',
    // Malaysia
    'Asia/Kuala_Lumpur': 'MY',
    // Indonesia
    'Asia/Jakarta': 'ID', 'Asia/Makassar': 'ID', 'Asia/Jayapura': 'ID',
    // Philippines
    'Asia/Manila': 'PH',
    // UAE
    'Asia/Dubai': 'AE',
    // Pakistan
    'Asia/Karachi': 'PK',
    // Bangladesh
    'Asia/Dhaka': 'BD',
    // Sri Lanka
    'Asia/Colombo': 'LK',
    // Turkey
    'Europe/Istanbul': 'TR',
    // UK
    'Europe/London': 'GB',
    // Germany
    'Europe/Berlin': 'DE',
    // France
    'Europe/Paris': 'FR',
    // Italy
    'Europe/Rome': 'IT',
    // Spain
    'Europe/Madrid': 'ES',
    // Netherlands
    'Europe/Amsterdam': 'NL',
    // Belgium
    'Europe/Brussels': 'BE',
    // Austria
    'Europe/Vienna': 'AT',
    // Switzerland
    'Europe/Zurich': 'CH',
    // Sweden
    'Europe/Stockholm': 'SE',
    // Norway
    'Europe/Oslo': 'NO',
    // Denmark
    'Europe/Copenhagen': 'DK',
    // Finland
    'Europe/Helsinki': 'FI',
    // Poland
    'Europe/Warsaw': 'PL',
    // Ireland
    'Europe/Dublin': 'IE',
    // Greece
    'Europe/Athens': 'GR',
    // Portugal
    'Europe/Lisbon': 'PT',
    // Brazil
    'America/Sao_Paulo': 'BR', 'America/Rio_Branco': 'BR', 'America/Manaus': 'BR',
    'America/Fortaleza': 'BR', 'America/Recife': 'BR', 'America/Bahia': 'BR',
    // Mexico
    'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Monterrey': 'MX',
    'America/Tijuana': 'MX',
    // Canada
    'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
    'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA',
    // Australia
    'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
    'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU', 'Australia/Hobart': 'AU',
    'Australia/Darwin': 'AU',
    // New Zealand
    'Pacific/Auckland': 'NZ',
    // South Africa
    'Africa/Johannesburg': 'ZA',
    // Nigeria
    'Africa/Lagos': 'NG',
    // Kenya
    'Africa/Nairobi': 'KE',
}

// baby: Map navigator.language region subtags to country codes
// e.g. "en-IN" → "IN", "pt-BR" → "BR"
function getCountryFromLanguage(): string | null {
    try {
        const languages = navigator.languages || [navigator.language]
        for (const lang of languages) {
            // Look for region subtag like "en-IN", "pt-BR", "zh-TW"
            const parts = lang.split('-')
            if (parts.length >= 2) {
                const region = parts[parts.length - 1].toUpperCase()
                // Validate it's a 2-letter country code that we support
                if (region.length === 2 && CURRENCY_BY_COUNTRY[region]) {
                    return region
                }
            }
        }
    } catch {
        // navigator not available
    }
    return null
}

// baby: Detect country from IANA timezone
function getCountryFromTimezone(): string | null {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (tz && TIMEZONE_TO_COUNTRY[tz]) {
            return TIMEZONE_TO_COUNTRY[tz]
        }
    } catch {
        // Intl not available
    }
    return null
}

/**
 * Format a USD price into the target currency
 * Handles rounding intelligently based on the currency
 */
export function formatPrice(usdAmount: number, currency: CurrencyInfo): string {
    if (usdAmount === 0) return currency.position === 'suffix' ? `0 ${currency.symbol}` : `${currency.symbol}0`

    const converted = usdAmount * currency.rate

    // Smart rounding based on currency magnitude
    let formatted: string
    if (currency.rate >= 100) {
        // High-value currencies (JPY, KRW, IDR, etc.) - round to nearest whole number
        const rounded = Math.round(converted)
        formatted = rounded.toLocaleString(currency.locale)
    } else if (currency.rate >= 10) {
        // Medium-value currencies (INR, MXN, THB, etc.) - round to nearest whole number
        const rounded = Math.round(converted)
        formatted = rounded.toLocaleString(currency.locale)
    } else if (currency.rate >= 1) {
        // Similar-value currencies (CAD, AUD, SGD, etc.) - keep 2 decimal places
        formatted = converted.toFixed(2)
    } else {
        // Lower-value currencies (EUR, GBP, CHF) - keep 2 decimal places
        formatted = converted.toFixed(2)
    }

    if (currency.position === 'suffix') {
        return `${formatted} ${currency.symbol}`
    }

    return `${currency.symbol}${formatted}`
}

/**
 * Hook to detect user's country and provide currency conversion.
 * 
 * Uses ONLY browser-native APIs (no external network calls):
 *   1. Intl.DateTimeFormat timezone (e.g. "Asia/Kolkata" → India)
 *   2. navigator.language region subtag (e.g. "en-IN" → India)
 * 
 * Falls back to USD if detection fails.
 * No location permission needed. No CSP issues. Instant detection.
 */
export function useCurrency() {
    const [currency, setCurrency] = useState<CurrencyInfo>(USD_CURRENCY)
    const [countryCode, setCountryCode] = useState<string>('US')
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Priority 1: Timezone-based detection (most reliable)
        const tzCountry = getCountryFromTimezone()
        if (tzCountry && CURRENCY_BY_COUNTRY[tzCountry]) {
            setCurrency(CURRENCY_BY_COUNTRY[tzCountry])
            setCountryCode(tzCountry)
            setIsLoading(false)
            return
        }

        // Priority 2: Language/locale-based detection
        const langCountry = getCountryFromLanguage()
        if (langCountry && CURRENCY_BY_COUNTRY[langCountry]) {
            setCurrency(CURRENCY_BY_COUNTRY[langCountry])
            setCountryCode(langCountry)
            setIsLoading(false)
            return
        }

        // Fallback: USD (already set as default)
        setIsLoading(false)
    }, [])

    const formatPriceFn = useCallback(
        (usdAmount: number) => formatPrice(usdAmount, currency),
        [currency]
    )

    return {
        currency,
        countryCode,
        isLoading,
        formatPrice: formatPriceFn,
    }
}
